import { api } from "@/lib/api";
import { PIPELINE } from "@/pages/admin/eventos/eventosUtils";

export const rid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : Math.random().toString(36).slice(2));

export const KOLONNER = [["maal", "Mål"], ["status", "Status"], ["ansvarlig", "Owner"], ["neste", "Next move"], ["frist", "Due"], ["verdi", "Verdi"]];
export const SPEAKER_STAGES = PIPELINE.map((s) => [s.k, s.label]);
export const PARTNER_STAGES = [
  ["researching", "Researching"], ["ready", "Ready to contact"], ["outreach_sent", "Outreach sent"],
  ["waiting", "Waiting"], ["dialogue", "Dialogue"], ["interested", "Interested"],
  ["confirmed", "Confirmed"], ["declined", "Declined"],
];
export const ROUTE_TYPES = [["", "—"], ["warm", "Warm intro"], ["direct", "Direct"], ["management", "Management"], ["corporate", "Corporate"], ["research", "Research"]];
export const ROUTE_LABEL = Object.fromEntries(ROUTE_TYPES);
export const BOOKING_STAGES = [["", "—"], ["date_hold", "Date hold"], ["terms", "Terms"], ["contracted", "Contracted"]];
export const CONTRACT_STAGES = [["", "—"], ["proposal", "Proposal"], ["verbal", "Verbal interest"], ["signed", "Signed"], ["activation", "Activation planning"]];
export const FIRMA_KATEGORIER = [
  "Banking / Finance", "Consulting / Transformation", "Frontier AI", "Enterprise AI / Software",
  "Cloud / Infrastructure", "Data / AI", "Cybersecurity", "Industrial / Energy",
  "Robotics / Autonomy", "Media / Broadcast", "Legal / Policy", "Hospitality",
  "Maritime", "AI-native Software",
];
export const PARTNER_ROLLER = [
  "Founding / Presenting", "Strategic Partner", "Category Partner", "Frontier AI Relationship",
  "AI Experience Partner", "Broadcast Partner", "Summit Dinner Partner", "Media Partner",
];
export const PARTNERSKAP_KATEGORIER = [
  "AI Law & Policy", "Banking & Finance", "Payments & Fintech", "AI Transformation",
  "Cloud & Infrastructure", "AI Compute & Infrastructure", "Data & AI", "Cybersecurity",
  "Industrial AI", "Robotics & Autonomy", "Maritime AI", "Energy & AI",
  "AI-native Software", "Enterprise & Agentic AI", "Broadcast & Media", "Hospitality",
  "Telecom & Sovereign AI",
];
export const SPEAKER_ROLLER = [
  "Headliner", "Headliner / draw", "Headliner / wildcard", "Moonshot headliner",
  "Frontier anchor", "Anchor", "Anchor / credibility", "Anchor / moonshot",
  "Credibility", "Applied AI", "Applied AI anchor", "Executive anchor",
  "AI/compute anchor", "Moderator", "Wildcard",
];
export const TARGET_TYPER = [["person", "Person"], ["org", "Organisation"]];

export const nyPipelineSpec = () => ({ bid: rid(), modus: "kuratert", kilde: "speakers", items: [], filter: { stage: "" }, visning: "tabell", kolonner: ["maal", "status", "ansvarlig", "frist"], tittel: "" });
export const nySakerSpec = () => ({ bid: rid(), modus: "koblet", ids: [], kunAapne: true, tittel: "" });
export const nyEntitetSpec = () => ({ bid: rid(), kilde: "", entityId: "" });

export async function hentBlokkData(spec, docId) {
  const { data } = await api.post("/admin/docs/blokk-data", { specs: [spec], docId: docId || null });
  return data.data?.[spec.bid] || [];
}

export async function blokkSok(q, kilder) {
  const { data } = await api.get("/admin/docs/blokk-sok", { params: { q, kilder } });
  return data.treff || [];
}

let _medlCache = null;
export async function hentMedlemmer() {
  if (_medlCache) return _medlCache;
  const { data } = await api.get("/admin/task-members");
  _medlCache = Array.isArray(data) ? data : data.members || [];
  return _medlCache;
}

export function patchEier(r, memberId) {
  if (r.type === "task") return api.patch(`/admin/tasks/${r.id}`, { assigneeId: memberId });
  if (r.type === "kontakt") return api.patch(`/admin/os/kontakter/${r.id}`, { ownerId: memberId });
  if (r.type === "partner") return api.patch(`/admin/os/partners/${r.id}`, { ownerId: memberId });
  return api.patch(`/admin/os/speakers/${r.id}`, { ownerId: memberId });
}

export function patchNeste(r, nextStep, due) {
  if (r.type === "kontakt") return api.patch(`/admin/os/kontakter/${r.id}`, { nextStep, nextStepDue: due || null });
  if (r.type === "partner") return api.patch(`/admin/os/partners/${r.id}`, { nextStep, nextStepDue: due || null });
  return api.patch(`/admin/os/speakers/${r.id}`, { nextStep, followUp: due || null });
}

export function patchInfo(r, felter) {
  if (r.type === "kontakt") return api.patch(`/admin/os/kontakter/${r.id}`, felter);
  if (r.type === "partner") return api.patch(`/admin/os/partners/${r.id}`, felter);
  return api.patch(`/admin/os/speakers/${r.id}`, felter);
}

export const recordRute = (r) => {
  if (r.type === "speaker" || r.kilde === "speakers") return `/admin/speakers?aapne=${r.id}`;
  if (r.type === "partner" || r.kilde === "partners") return `/admin/partners?aapne=${r.id}`;
  return `/admin/saker?aapne=${r.id}`;
};

export const fmtFrist = (d) => (d ? new Date(d).toLocaleDateString("nb-NO", { day: "numeric", month: "short" }) : "—");
export const fmtVerdi = (v) => (v ? `${Math.round(v).toLocaleString("nb-NO")} kr` : "—");

const STAGE_FARGE = {
  researching: "#98A2B3", ready: "#F59E0B", outreach_sent: "#6366F1", waiting: "#D97706",
  dialogue: "#0EA5E9", interested: "#D97706", confirmed: "#10B981", declined: "#DC2626",
  target: "#0EA5E9", contacted: "#6366F1",
  lead: "#98A2B3", signed: "#10B981", contracted: "#10B981",
  inbox: "#98A2B3", todo: "#0EA5E9", doing: "#D97706", done: "#10B981",
  not_started: "#98A2B3", intro_needed: "#D97706", in_contact: "#6366F1",
  waiting: "#D97706", dialogue: "#10B981",
};
export const stageDot = (stage) => STAGE_FARGE[stage] || "#98A2B3";

export const STATUS_EN = {
  researching: "Researching", ready: "Ready to contact", outreach_sent: "Outreach sent",
  waiting: "Waiting", dialogue: "Dialogue", interested: "Interested",
  confirmed: "Confirmed", declined: "Declined",
  target: "To contact", lead: "To contact", contacted: "Outreach sent",
  not_started: "Not started", intro_needed: "Intro needed", in_contact: "Contacted",
  done: "Done",
};
const TONE_GRONN = new Set(["dialogue", "interested", "confirmed", "done", "signed", "contracted", "verbal"]);
const TONE_BLAA = new Set(["outreach_sent", "contacted", "in_contact"]);
const TONE_GUL = new Set(["ready", "waiting", "intro_needed", "proposal"]);
export const statusTone = (stage) => (stage === "declined" ? "rod"
  : TONE_GRONN.has(stage) ? "gronn"
    : TONE_GUL.has(stage) ? "gul"
      : TONE_BLAA.has(stage) ? "blaa" : "graa");
export const statusEn = (r) => STATUS_EN[r.stage] || r.stageLabel;

export const KONTAKT_KATEGORIER = [
  ["styret", "Styret"], ["byraa", "Markedsføringsbyrå"], ["leverandor", "Leverandører"],
  ["presse", "Presse & media"], ["annet", "Annet"],
];
export const KONTAKT_STATUSER = [
  ["not_started", "Not started"], ["intro_needed", "Intro needed"], ["in_contact", "Contacted"],
  ["waiting", "Waiting"], ["dialogue", "Dialogue"], ["done", "Done"],
];

export const SPOR_GOAL = {
  frontier: "First real dialogue with one frontier AI company",
  headliner: "One commercially viable headliner route",
  anchor: "First meaningful commercial conversation",
  program: "Backbone of the programme secured",
  experience: "2–3 selective activations",
};

export const FOKUS = [["now", "Now"], ["next", "Next"], ["watch", "Watch"]];
export const SPOR = [["frontier", "Frontier AI"], ["headliner", "Global Headliner"], ["anchor", "Norwegian Anchor"]];
export const SPOR_ALLE = [...SPOR, ["program", "Program anchors"], ["experience", "AI Experience"]];
export const MILEPAEL = {
  frontier: "First real dialogue booked",
  headliner: "Indicative fee + soft hold",
  anchor: "First substantive commercial meeting",
};

export function fmtDue(d) {
  if (!d) return null;
  const dato = new Date(`${String(d).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(dato.getTime())) return null;
  const naa = new Date();
  naa.setHours(12, 0, 0, 0);
  const diff = Math.round((dato - naa) / 86400000);
  if (diff < 0) return { label: "Overdue", tone: "overdue" };
  if (diff === 0) return { label: "Today", tone: "today" };
  if (diff === 1) return { label: "Tomorrow", tone: "soon" };
  return { label: dato.toLocaleDateString("en-GB", { day: "numeric", month: "short" }), tone: "" };
}

export const SIKRET_STAGES = new Set(["confirmed", "signed", "contracted", "done"]);
export const PASSIVE_STAGES = new Set(["researching", "target", "lead", "declined", "", "not_started"]);
export const erAktivStage = (stage) => !PASSIVE_STAGES.has(stage || "") && !SIKRET_STAGES.has(stage);
export const PRI_LABEL = { P1: "P1 — Critical", P2: "P2 — High", P3: "P3 — Watch", M: "Moonshot" };
export const PRI_VALG = [["P1", "P1"], ["P2", "P2"], ["P3", "P3"], ["M", "Moonshot"]];
